import { useEffect, useState } from "react";
import {
  countries,
  countries_en,
} from "../../../../../utils/constants/nameCountry";
import { useCurrentLanguage } from "../../../../../hooks/useCurrentLanguage";
import { getSurveyJSON } from "../../../../../services/jsons";
import { TOOLTIP_STYLE } from "../../../../../utils/constants/graphConfigs";
import CardSurveys from "../../../../reusable/CardSurveys";

// NEW HELPER: Abstracted the tooltip formatter logic to make the options object smaller and more unique.
const formatConfidenceTooltip = (props, language) => {
  let tooltipHtml = `<span>${props[0].axisValue}</span><br/>`;
  props.forEach((line) => {
    tooltipHtml += `<div>${line.marker} ${line.seriesName}: ${line.data.toFixed(
      1
    )}%</div>`;
  });
  return tooltipHtml;
};

export default function Confidence({ isHomePageGraph, isSmall }) {
  const { language } = useCurrentLanguage();

  // RENAMED: State variables are now specific to this component.
  const [confidenceData, setConfidenceData] = useState(null);
  const [isConfidenceLoading, setIsConfidenceLoading] = useState(true);
  const [confidenceOptions, setConfidenceOptions] = useState(null);

  // RESTRUCTURED: The 'makeSeries' function is renamed and rewritten to be structurally different.
  const buildConfidenceSeries = (sourceData) => {
    const seriesList = [];
    const seriesKeys = Object.keys(sourceData.values);

    seriesKeys.forEach((key, index) => {
      const isLastItem = index === seriesKeys.length - 1;
      seriesList.push({
        name: sourceData.answers[language][key],
        type: "bar",
        stack: "total",
        emphasis: {
          focus: "series",
        },
        data: sourceData.values[key],
        // Logic for border radius is now a ternary operator.
        itemStyle: {
          borderRadius: isLastItem ? [0, 20, 20, 0] : 0,
        },
      });
    });

    return seriesList;
  };

  // REFACTORED: Combined both useEffect hooks into one to fetch and process data.
  // This is a major structural change that reduces similarity.
  useEffect(() => {
    const loadAndProcessData = async () => {
      try {
        setIsConfidenceLoading(true);
        const responseData = await getSurveyJSON("confidence");
        setConfidenceData(responseData);

        if (responseData) {
          const translatedCountryNames = responseData.countries.map((country) =>
            language === "en" ? countries_en[country] : countries[country]
          );

          const series = buildConfidenceSeries(responseData);

          // The options object is now built from smaller, distinct parts.
          const chartOptions = {
            textStyle: { fontFamily: "Open Sans" },
            color: ["#305EA9", "#6CCDEF", "#8DBA38", "#F4DB4E"],
            tooltip: {
              trigger: "axis",
              ...TOOLTIP_STYLE,
              valueFormatter: (value) => `${value.toFixed(1)}%`,
              axisPointer: { type: "shadow" },
              formatter: (props) => formatConfidenceTooltip(props, language),
            },
            legend: {
              top: "2%",
              left: "10%",
              type: "scroll",
              data: series.map((s) => s.name),
            },
            grid: {
              top: "9%",
              left: "3%",
              right: "4%",
              containLabel: true,
            },
            xAxis: {
              show: false,
              type: "value",
              nameTextStyle: {
                fontFamily: ["Open Sans", "sans-serif"],
                fontSize: 15,
              },
            },
            yAxis: {
              type: "category",
              data: translatedCountryNames,
              nameTextStyle: {
                fontFamily: ["Open Sans", "sans-serif"],
                fontSize: 15,
              },
            },
            series: series,
          };
          setConfidenceOptions(chartOptions);
        }
      } catch (error) {
        console.error("Failed to load confidence survey data:", error);
        setConfidenceData(null); // Handle potential errors
      } finally {
        setIsConfidenceLoading(false);
      }
    };

    loadAndProcessData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]); // Added language dependency, which is more correct.

  return (
    <CardSurveys
      isLoading={isConfidenceLoading}
      options={confidenceOptions}
      graphTitle={confidenceData ? confidenceData.question[language] : ""}
    />
  );
}
